import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../api/client';
import Logo from '../components/Logo';

// Abstract map SVG of Severna Park, MD area - colorful version
const MapBackground = () => (
  <svg
    className="absolute inset-0 w-full h-full"
    viewBox="0 0 800 600"
    preserveAspectRatio="xMidYMid slice"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e0e7ff" strokeWidth="0.5" />
      </pattern>
      <linearGradient id="waterGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#38bdf8" />
        <stop offset="50%" stopColor="#0ea5e9" />
        <stop offset="100%" stopColor="#06b6d4" />
      </linearGradient>
      <linearGradient id="parkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#4ade80" />
        <stop offset="100%" stopColor="#22c55e" />
      </linearGradient>
    </defs>

    {/* Base with subtle warm tint */}
    <rect width="100%" height="100%" fill="#fefce8" />
    <rect width="100%" height="100%" fill="url(#grid)" />

    {/* Severn River - vibrant blue flowing through */}
    <path
      d="M 0 200 Q 150 180 250 220 Q 400 280 500 240 Q 650 180 800 200"
      fill="none"
      stroke="url(#waterGradient)"
      strokeWidth="70"
      opacity="0.5"
    />
    <path
      d="M 0 200 Q 150 180 250 220 Q 400 280 500 240 Q 650 180 800 200"
      fill="none"
      stroke="#38bdf8"
      strokeWidth="35"
      opacity="0.6"
    />

    {/* Magothy River area - top right */}
    <path
      d="M 600 0 Q 650 80 700 60 Q 780 40 800 80"
      fill="none"
      stroke="#22d3ee"
      strokeWidth="45"
      opacity="0.5"
    />

    {/* Major roads - warm terracotta colors */}
    <path d="M 100 0 L 100 600" fill="none" stroke="#fb923c" strokeWidth="4" opacity="0.5" />
    <path d="M 300 0 Q 320 300 280 600" fill="none" stroke="#f97316" strokeWidth="5" opacity="0.6" />
    <path d="M 500 0 L 520 600" fill="none" stroke="#fb923c" strokeWidth="4" opacity="0.5" />
    <path d="M 0 400 Q 400 380 800 420" fill="none" stroke="#f97316" strokeWidth="5" opacity="0.6" />
    <path d="M 0 100 Q 200 120 400 90 Q 600 60 800 100" fill="none" stroke="#fdba74" strokeWidth="4" opacity="0.5" />

    {/* B&A Trail - bright green dashed */}
    <path
      d="M 50 600 Q 100 500 150 400 Q 200 300 180 200 Q 160 100 200 0"
      fill="none"
      stroke="#22c55e"
      strokeWidth="8"
      strokeDasharray="15 8"
      opacity="0.7"
    />

    {/* Neighborhood clusters - fun colorful bubbles */}
    <circle cx="350" cy="350" r="85" fill="#a78bfa" opacity="0.3" />
    <circle cx="350" cy="350" r="50" fill="#c4b5fd" opacity="0.4" />

    <circle cx="450" cy="450" r="65" fill="#fb7185" opacity="0.3" />
    <circle cx="450" cy="450" r="35" fill="#fda4af" opacity="0.4" />

    <circle cx="250" cy="480" r="55" fill="#fbbf24" opacity="0.3" />
    <circle cx="250" cy="480" r="30" fill="#fcd34d" opacity="0.4" />

    <circle cx="550" cy="350" r="75" fill="#34d399" opacity="0.3" />
    <circle cx="550" cy="350" r="45" fill="#6ee7b7" opacity="0.4" />

    <circle cx="150" cy="350" r="50" fill="#60a5fa" opacity="0.3" />
    <circle cx="150" cy="350" r="28" fill="#93c5fd" opacity="0.4" />

    <circle cx="650" cy="450" r="45" fill="#f472b6" opacity="0.3" />
    <circle cx="650" cy="450" r="25" fill="#f9a8d4" opacity="0.4" />

    {/* Park areas - bright green spaces */}
    <ellipse cx="400" cy="320" rx="35" ry="22" fill="url(#parkGradient)" opacity="0.5" />
    <ellipse cx="280" cy="420" rx="28" ry="18" fill="#4ade80" opacity="0.5" />
    <ellipse cx="520" cy="380" rx="24" ry="28" fill="#22c55e" opacity="0.45" />
    <ellipse cx="180" cy="480" rx="20" ry="15" fill="#86efac" opacity="0.5" />
    <ellipse cx="600" cy="320" rx="22" ry="18" fill="#4ade80" opacity="0.45" />

    {/* Small connector roads - softer */}
    <path d="M 200 300 Q 250 320 300 300" fill="none" stroke="#fed7aa" strokeWidth="3" opacity="0.6" />
    <path d="M 400 350 Q 450 370 500 350" fill="none" stroke="#fed7aa" strokeWidth="3" opacity="0.6" />
    <path d="M 350 400 L 350 500" fill="none" stroke="#fde68a" strokeWidth="3" opacity="0.5" />
    <path d="M 450 380 L 480 480" fill="none" stroke="#fde68a" strokeWidth="3" opacity="0.5" />

    {/* Decorative dots for points of interest */}
    <circle cx="320" cy="370" r="8" fill="#f43f5e" opacity="0.7" />
    <circle cx="420" cy="340" r="8" fill="#8b5cf6" opacity="0.7" />
    <circle cx="500" cy="400" r="8" fill="#0ea5e9" opacity="0.7" />
    <circle cx="280" cy="450" r="8" fill="#f59e0b" opacity="0.7" />
    <circle cx="380" cy="420" r="8" fill="#10b981" opacity="0.7" />
    <circle cx="550" cy="380" r="8" fill="#ec4899" opacity="0.7" />
  </svg>
);

type Step = 'email' | 'admin-password' | 'store-magic-link' | 'magic-link-sent';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<Step>('email');

  const { loginWithCredentials } = useAuth();
  const navigate = useNavigate();

  const handleEmailSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiClient.post<{ success: boolean; data: { userType: string } }>('/auth/check-email', { email });
      const { userType } = response.data.data;

      if (userType === 'admin') {
        setStep('admin-password');
      } else if (userType === 'store') {
        // Send magic link automatically for store owners
        await apiClient.post('/auth/store/magic-link', { email });
        setStep('magic-link-sent');
      } else {
        setError('This email is not registered. Please contact your administrator.');
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await loginWithCredentials(email, password);
      navigate('/admin/dashboard');
    } catch (err: any) {
      setError(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('email');
    setPassword('');
    setError('');
  };

  const handleSendMagicLink = async () => {
    setLoading(true);
    setError('');
    try {
      await apiClient.post('/auth/store/magic-link', { email });
      setStep('magic-link-sent');
    } catch (err: any) {
      setError(err.message || 'Failed to send login link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 relative overflow-hidden">
      {/* Map Background */}
      <div className="absolute inset-0">
        <MapBackground />
      </div>

      {/* Gradient overlay for better readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/70 via-white/50 to-amber-50/30" />

      <div className="max-w-md w-full relative z-10">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo size="xl" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Discover The Park</h1>
          <p className="mt-1 text-lg text-gray-600">Activity Planner</p>
        </div>

        <div className="card backdrop-blur-sm bg-white/95 shadow-xl border border-white/50">
          {/* Step 1: Email */}
          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="label">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  placeholder="you@example.com"
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full"
              >
                {loading ? 'Checking...' : 'Continue'}
              </button>
            </form>
          )}

          {/* Step 2a: Admin Password */}
          {step === 'admin-password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-6">
              <div className="text-center mb-4">
                <p className="text-sm text-gray-600">Signing in as</p>
                <p className="font-medium text-gray-900">{email}</p>
              </div>

              {error && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="password" className="label">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input"
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>

              <button
                type="button"
                onClick={handleSendMagicLink}
                disabled={loading}
                className="btn-secondary w-full"
              >
                {loading ? 'Sending...' : 'Send me a login link instead'}
              </button>

              <button
                type="button"
                onClick={handleBack}
                className="text-sm text-gray-500 hover:text-gray-700 w-full text-center"
              >
                Use a different email
              </button>
            </form>
          )}

          {/* Step 2b: Magic Link Sent */}
          {(step === 'magic-link-sent' || step === 'store-magic-link') && (
            <div className="text-center space-y-6">
              <div className="text-5xl mb-4">📧</div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Check your email</h2>
                <p className="text-gray-600">
                  We sent a login link to <strong>{email}</strong>
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Click the link in the email to sign in.
                </p>
              </div>

              {error && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div className="pt-4 space-y-3">
                <button
                  onClick={handleSendMagicLink}
                  disabled={loading}
                  className="btn-secondary w-full"
                >
                  {loading ? 'Sending...' : 'Resend Email'}
                </button>

                <button
                  onClick={handleBack}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Use a different email
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-500 mt-6">
          Severna Park, Maryland
        </p>
      </div>
    </div>
  );
}
